import { useCallback, useEffect, useState } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import type { SprintField } from '@atlassian/jira-business-common/src/common/types/sprint.tsx';
import { getVariables } from '@atlassian/jira-business-common/src/common/utils/get-variables/index.tsx';
import type { BusinessIssueField } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/types.tsx';
import type { GenericVariables } from '@atlassian/jira-business-list-handlers/src/common/types.tsx';
import { isGroupItem } from '@atlassian/jira-business-list-handlers/src/common/utils/hierarchy/index.tsx';
import { useCacheActions } from '@atlassian/jira-business-list-handlers/src/controllers/list-cache/main.tsx';
import type { BaseIssueNode } from '@atlassian/jira-business-list-handlers/src/controllers/list-cache/types.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useFlagService } from '@atlassian/jira-flags';
import { defaultSaveField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/utils.tsx';
import { getAriConfig, isAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import { JiraPriorityAri } from '@atlassian/ari/jira/priority';
import {
	ASSIGNEE_TYPE,
	PRIORITY_TYPE,
	CATEGORY_TYPE,
	SPRINT_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { toIssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { getFieldForAnalytics } from '../../common/utils/get-field-for-analytics/index.tsx';
import type {
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraSingleSelectUserPickerField as AssigneeField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraPriorityField as PriorityField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraSingleSelectField as CategoryField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraNumberField as NumberField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraSprintField as JiraSprintField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraSprintField_selectedSprintsConnection_edges_node as JiraSprintFieldNode,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields as ListItemField,
} from '../../services/get-list-view-issues/__generated_apollo__/JWMListViewItems';
import { LIST_VIEW_QUERY } from '../../services/get-list-view-issues/gql.tsx';
import { AccountStatus } from '../../services/get-list-view-issues/types.tsx';
import { useGroupCacheActions } from '../group-cache-actions/index.tsx';
import { useHierarchyStore } from '../hierarchy/hierarchy-store/index.tsx';
import type { ListViewControllerData } from '../list-view-issues/types.tsx';
import { useRankParent } from '../rank-parent/index.tsx';
import { useIssuesInput } from '../use-issues-input/index.tsx';
import { ASSIGNEE_SEARCH_QUERY_NAME, UPDATE_FIELD_QUERY_NAME } from './constants.tsx';
import messages from './messages.tsx';
import type {
	UserFieldValue,
	PriorityFieldValue,
	FieldValue,
	CategoryFieldValue,
} from './types.tsx';

export const useUpdateGroupIssue = () => {
	const { getCachedIssueById } = useCacheActions();
	const { addToGroupCache } = useGroupCacheActions();
	const [{ rankParentIssueId }] = useRankParent();
	const input = useIssuesInput({
		groupIssueId: isGroupItem(rankParentIssueId) ? rankParentIssueId : undefined,
		parentIssueId: !isGroupItem(rankParentIssueId) ? rankParentIssueId : undefined,
	});
	const { showFlag } = useFlagService();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [originalIssue, setOriginalIssue] = useState<BaseIssueNode | null>();
	const [{ issuesMap }] = useHierarchyStore();
	const [updateError, setUpdateError] = useState<{
		error: Error;
		field: string;
		query: string;
	} | null>(null);
	const cloudId = useCloudId();

	useEffect(() => {
		if (updateError) {
			setUpdateError(null);

			// revert optimistic update
			if (originalIssue) {
				addToGroupCache([originalIssue]);
				setOriginalIssue(null);
			}

			showFlag({
				type: 'error',
				title: [messages.errorTitle, { field: updateError.field }],
				description: [
					expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? messages.errorMessageIssueTermRefresh
						: messages.errorMessage,
					{ field: updateError.field },
				],
				messageId: 'business-list.controllers.update-group-issue.show-flag.error',
				messageType: 'transactional',
			});

			fireErrorAnalytics({
				meta: {
					id: updateError.query,
					packageName: 'jiraBusinessList',
					teamName: 'keystone',
				},
				error: updateError.error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	}, [originalIssue, updateError, addToGroupCache, showFlag]);

	const optimisticUpdate = useCallback(
		(issue: ListViewControllerData, fieldType: string, field: ListItemField) => {
			const cachedIssue = getCachedIssueById({
				issueId: issue.issueId,
				query: LIST_VIEW_QUERY,
				variables: {
					...getVariables(input),
					cloudId,
				},
			});
			setOriginalIssue(cachedIssue);
			if (cachedIssue) {
				const updatedIssue = {
					...cachedIssue,
					fields: [
						...(cachedIssue.fields?.filter(
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
							(cachedField: any) => cachedField.fieldId !== fieldType,
						) ?? []),
						field,
					],
				};
				addToGroupCache([updatedIssue]);
			}
		},
		[getCachedIssueById, input, cloudId, addToGroupCache],
	);

	const bulkOptimisticUpdate = useCallback(
		(issueIds: number[], fields: ListItemField[]) => {
			const cachedIssues = issueIds
				.map((issueId) =>
					getCachedIssueById({
						issueId: issueId.toString(),
						query: LIST_VIEW_QUERY,
						variables: {
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							...(issuesMap.get(issueId.toString())?.cacheVariables as GenericVariables),
							cloudId,
						},
					}),
				)
				.filter(Boolean);
			const fieldIds = fields.map((field) => field.fieldId);
			if (cachedIssues.length) {
				const updatedIssues = cachedIssues.map((cachedIssue) => ({
					...cachedIssue,
					fields: [
						...(cachedIssue.fields?.filter(
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
							(cachedField: any) => !fieldIds.includes(cachedField.fieldId),
						) ?? []),
						...fields,
					],
				}));
				addToGroupCache(updatedIssues);
			}
		},
		[getCachedIssueById, issuesMap, cloudId, addToGroupCache],
	);

	const updateField = useCallback(
		(
			issue: ListViewControllerData,
			fieldType: string,
			fieldValue: FieldValue,
			analyticsAlias?: string,
		) => {
			defaultSaveField(toIssueKey(issue.key), fieldType, fieldValue, '')
				.then(() => {
					fireOperationalAnalytics(
						createAnalyticsEvent({}),
						`${UPDATE_FIELD_QUERY_NAME} succeeded`,
						{ field: getFieldForAnalytics(fieldType, analyticsAlias) },
					);
				})
				.catch((error) => {
					setUpdateError({
						error,
						field: getFieldForAnalytics(fieldType, analyticsAlias),
						query: UPDATE_FIELD_QUERY_NAME,
					});
				});
		},
		[setUpdateError, createAnalyticsEvent],
	);

	const updateAssignee = useCallback(
		(issue: ListViewControllerData, fieldValue?: UserFieldValue | null) => {
			const newField: AssigneeField = {
				aliasFieldId: null,
				fieldId: ASSIGNEE_TYPE,
				name: 'assignee',
				type: 'assignee',
				fieldConfig: {
					isEditable: true,
					__typename: 'JiraFieldConfig',
				},
				__typename: 'JiraSingleSelectUserPickerField',
				user:
					fieldValue != null
						? {
								accountId: fieldValue.accountId,
								accountStatus: AccountStatus.active,
								picture: fieldValue.avatarUrl,
								name: fieldValue.displayName,
								__typename: 'AtlassianAccountUser',
							}
						: null,
			};
			optimisticUpdate(issue, ASSIGNEE_TYPE, newField);
			if (fieldValue === null || fieldValue === undefined) {
				// unassigned user
				updateField(issue, ASSIGNEE_TYPE, null);
			} else {
				performGetRequest(
					`/rest/api/2/user/assignable/search?issueKey=${issue.key}&accountId=${fieldValue.accountId}`,
				)
					.then((response) => {
						fireOperationalAnalytics(
							createAnalyticsEvent({}),
							`${ASSIGNEE_SEARCH_QUERY_NAME} succeeded`,
						);

						if (Array.isArray(response) && response[0]) {
							updateField(issue, ASSIGNEE_TYPE, response[0]);
						} else {
							setUpdateError({
								error: new Error(
									'User does not exist or is not allowed to be assigned to the issue',
								),
								field: ASSIGNEE_TYPE,
								query: ASSIGNEE_SEARCH_QUERY_NAME,
							});
						}
					})
					.catch((error) => {
						setUpdateError({
							error,
							field: ASSIGNEE_TYPE,
							query: ASSIGNEE_SEARCH_QUERY_NAME,
						});
					});
			}
		},
		[optimisticUpdate, updateField, setUpdateError, createAnalyticsEvent],
	);

	const updatePriority = useCallback(
		(issue: ListViewControllerData, fieldValue?: PriorityFieldValue) => {
			if (fieldValue) {
				let priorityId =
					fieldValue.id && isAri(fieldValue.id)
						? getAriConfig(fieldValue.id).resourceId
						: fieldValue?.id ?? null;

				if (fg('migrate-deprecated-jira-platform-ari-fns')) {
					priorityId =
						fieldValue.id && JiraPriorityAri.check(fieldValue.id)
							? JiraPriorityAri.parse(fieldValue.id).priorityId
							: fieldValue?.id ?? null;
				}

				const newField: PriorityField = {
					aliasFieldId: null,
					fieldId: PRIORITY_TYPE,
					type: PRIORITY_TYPE,
					name: 'Priority',
					priority: {
						id: fieldValue.id ?? '',
						name: fieldValue.name,
						priorityId: priorityId ?? '',
						iconUrl: fieldValue.iconUrl,
						__typename: 'JiraPriority',
					},
					__typename: 'JiraPriorityField',
				};
				optimisticUpdate(issue, PRIORITY_TYPE, newField);

				updateField(issue, PRIORITY_TYPE, {
					...fieldValue,
					id: priorityId,
				});
			}
		},
		[optimisticUpdate, updateField],
	);

	const updateCategory = useCallback(
		(issue: ListViewControllerData, fieldKey: string, fieldValue?: CategoryFieldValue | null) => {
			const category = fieldValue ? { value: fieldValue.name, label: fieldValue.name } : null;

			const newField: CategoryField = {
				aliasFieldId: null,
				fieldId: fieldKey,
				fieldConfig: {
					isEditable: true,
					__typename: 'JiraFieldConfig',
				},
				__typename: 'JiraSingleSelectField',
				name: 'Category',
				type: CATEGORY_TYPE,
				fieldOption: fieldValue
					? {
							optionId: fieldValue.optionId,
							value: fieldValue.name,
							color: fieldValue.color
								? {
										colorKey: fieldValue.color,
										__typename: 'JiraColor',
									}
								: null,
							__typename: 'JiraOption',
						}
					: null,
			};

			optimisticUpdate(issue, fieldKey, newField);
			updateField(issue, fieldKey, category, 'category');
		},
		[optimisticUpdate, updateField],
	);

	const updateJiraNumber = useCallback(
		(issue: ListViewControllerData, field: BusinessIssueField, number?: number) => {
			if (typeof number !== 'undefined') {
				const newField: NumberField = {
					fieldId: field.id,
					aliasFieldId: field.aliasFieldId || null,
					name: field.name,
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					type: field.type as string,
					number,
					fieldConfig: {
						isEditable: true,
						__typename: 'JiraFieldConfig',
					},
					__typename: 'JiraNumberField',
				};

				optimisticUpdate(issue, field.id, newField);
				updateField(issue, field.id, number, newField.aliasFieldId || newField.type);
			}
		},
		[optimisticUpdate, updateField],
	);

	const updateSprint = useCallback(
		(issue: ListViewControllerData, field: BusinessIssueField, sprint?: SprintField | null) => {
			if (typeof sprint !== 'undefined') {
				const newField: JiraSprintField = {
					fieldId: field.id,
					name: field.name,
					aliasFieldId: 'sprint',
					type: SPRINT_TYPE,
					fieldConfig: {
						isEditable: true,
						__typename: 'JiraFieldConfig',
					},
					selectedSprintsConnection: {
						edges:
							sprint === null
								? []
								: [
										{
											node: {
												sprintId: String(sprint.id),
												name: sprint.name,
												// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
												state: sprint.state as JiraSprintFieldNode['state'],
												__typename: 'JiraSprint',
											},
											__typename: 'JiraSprintEdge',
										},
									],
						__typename: 'JiraSprintConnection',
					},
					__typename: 'JiraSprintField',
				};

				optimisticUpdate(issue, field.id, newField);
				updateField(issue, field.id, sprint === null ? null : Number(sprint.id), 'sprint');
			}
		},
		[optimisticUpdate, updateField],
	);

	return {
		updateAssignee,
		updatePriority,
		updateCategory,
		updateJiraNumber,
		updateSprint,
		bulkOptimisticUpdate,
	};
};
